import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Image as ImageIcon, X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

export default function Gallery({ 
  accent = "#115e32",
  pageTitle = "Gallery | University of Creative Technology Chittagong",
}) {
	const [images, setImages] = useState([]);
	const [currentIndex, setCurrentIndex] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const handlePost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				department_id:'0',
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },				
			});
			const response = await api.post("/gallery", payload);			
			
			// Loop data (array)
			setImages(response.data || []);
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {
		handlePost();
	}, []);
  
	const openLightbox = (index) => setCurrentIndex(index);
	const closeLightbox = () => setCurrentIndex(null);

	const prevImage = () =>
		setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
	const nextImage = () =>
		setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));

  return (
    <div>
      {/* Section Hero */}
      <section className="relative bg-gradient-to-r from-[#6BCCF2] to-teal-50 text-[#014864] py-20">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex justify-center mb-4"
          >
            <ImageIcon className="w-12 h-12 text-white" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            Image Gallery
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-lg opacity-90 max-w-2xl mx-auto"
          >
            Explore beautiful moments and memories at University of Creative Technology Chittagong.
          </motion.p>
        </div>

        {/* SVG Wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]">
          <svg
            className="relative block w-full h-20"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            viewBox="0 0 1200 120"
          >
            <path
              d="M985.66,92.83C906.67,72,
              823.78,31,743.84,14.19c-82.26-17.28-168.06-16.89-250.45.39-57.82,
              11.73-114,31.07-172,41.86..."
              fill="#014864"
            ></path>
          </svg>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="container mx-auto px-6 py-16">
        {images.length === 0 ? (
          <p className="text-center text-gray-500">Loading gallery...</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {images.map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                viewport={{ once: true }}
                onClick={() => openLightbox(i)}
                className="relative overflow-hidden rounded-xl shadow-lg group cursor-pointer"
              >
                <img
                  src={`${import.meta.env.VITE_API_SOURCE_URL}${img.picture}`}
                  alt={img.title}
                  className="w-full h-64 object-cover transform group-hover:scale-110 transition duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition">
                  <h3 className="text-lg font-semibold">{img.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {currentIndex !== null && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white hover:text-red-400"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Prev Button */}
            <button
              onClick={prevImage}
              className="absolute left-6 text-white hover:text-green-400"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>

            {/* Next Button */}
            <button
              onClick={nextImage}
              className="absolute right-6 text-white hover:text-green-400"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            {/* Image */}
            <motion.div
              key={currentIndex}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <img
                src={`${import.meta.env.VITE_API_SOURCE_URL}${images[currentIndex].picture}`}
                alt={images[currentIndex].title}
                className="max-h-[80vh] max-w-[90vw] rounded-lg shadow-lg mx-auto"
              />
              <p className="text-white mt-4 text-lg">
                {images[currentIndex].title}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
